import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from './cx';

interface C1LinkBase {
  children: ReactNode;
  /** Sit tight under a field (forgot-password rhythm). */
  tight?: boolean;
  className?: string;
}

type AsButton = C1LinkBase &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof C1LinkBase> & {
    href?: undefined;
    to?: undefined;
  };

type AsAnchor = C1LinkBase &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof C1LinkBase | 'href'> & {
    href: string;
    to?: undefined;
  };

type AsRouterLink = C1LinkBase &
  Omit<LinkProps, keyof C1LinkBase | 'to'> & {
    to: LinkProps['to'];
    href?: undefined;
  };

export type C1LinkProps = AsButton | AsAnchor | AsRouterLink;

/** Text / action link in brand blue. */
export const C1Link = forwardRef<HTMLButtonElement | HTMLAnchorElement, C1LinkProps>(
  function C1Link(props, ref) {
    const { children, tight = false, className } = props;
    const classes = cx('c1-link', tight && 'c1-link-tight', className);

    if ('to' in props && props.to !== undefined) {
      const { children: _c, tight: _t, className: _cl, to, href: _h, ...linkRest } = props;
      return (
        <Link ref={ref as React.Ref<HTMLAnchorElement>} to={to} className={classes} {...linkRest}>
          {children}
        </Link>
      );
    }

    if ('href' in props && typeof props.href === 'string') {
      const { children: _c, tight: _t, className: _cl, href, to: _to, ...anchorRest } = props;
      return (
        <a ref={ref as React.Ref<HTMLAnchorElement>} href={href} className={classes} {...anchorRest}>
          {children}
        </a>
      );
    }

    const {
      children: _c,
      tight: _t,
      className: _cl,
      type = 'button',
      ...buttonRest
    } = props as AsButton;

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        className={classes}
        {...buttonRest}
      >
        {children}
      </button>
    );
  },
);
